import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NotificationService } from '@progress/kendo-angular-notification';
import { DialogService } from '@progress/kendo-angular-dialog';
import {
  RouterAdminService,
  RouterTemplate,
  RouterStep,
  FeatureRouterMapping,
  FeatureRouterAction,
  FeatureRouterActionLabels,
  BitFeatureOption,
  WorkCenter
} from '../../../shared/services/router-admin.service';

@Component({
    selector: 'app-feature-router-mapping',
    templateUrl: './feature-router-mapping.component.html',
    styleUrls: ['./feature-router-mapping.component.css'],
    standalone: false
})
export class FeatureRouterMappingComponent implements OnInit {
  mappings: FeatureRouterMapping[] = [];
  filteredMappings: FeatureRouterMapping[] = [];
  loading = false;

  // Edit state
  isEditing = false;
  isNew = false;
  selectedMapping: FeatureRouterMapping | null = null;

  // Form
  mappingForm: FormGroup;

  // Reference data
  templates: RouterTemplate[] = [];
  steps: RouterStep[] = [];
  bitFeatures: BitFeatureOption[] = [];
  workCenters: WorkCenter[] = [];

  // Filters
  filterTemplateId: number | null = null;
  filterFeatureId: number | null = null;
  filterAction: FeatureRouterAction | null = null;
  searchText = '';

  // Action options
  actionOptions = [
    { value: FeatureRouterAction.AddStep, label: 'Add Step', icon: 'k-i-plus', description: 'Add a new step to the router' },
    { value: FeatureRouterAction.RemoveStep, label: 'Remove Step', icon: 'k-i-minus', description: 'Remove an existing step' },
    { value: FeatureRouterAction.ModifyStep, label: 'Modify Step', icon: 'k-i-edit', description: 'Modify properties of an existing step' },
    { value: FeatureRouterAction.InsertBefore, label: 'Insert Before', icon: 'k-i-sort-asc', description: 'Insert a new step before a specific step' },
    { value: FeatureRouterAction.InsertAfter, label: 'Insert After', icon: 'k-i-sort-desc', description: 'Insert a new step after a specific step' },
    { value: FeatureRouterAction.ReplaceStep, label: 'Replace Step', icon: 'k-i-arrows-swap', description: 'Replace an existing step with a new one' },
    { value: FeatureRouterAction.SkipStep, label: 'Skip Step', icon: 'k-i-arrow-right', description: 'Mark a step as not required' },
    { value: FeatureRouterAction.AppendInstructions, label: 'Append Instructions', icon: 'k-i-file-add', description: 'Add text to a step\'s instructions' }
  ];

  actionLabels = FeatureRouterActionLabels;

  constructor(
    private routerAdminService: RouterAdminService,
    private notificationService: NotificationService,
    private dialogService: DialogService,
    private fb: FormBuilder
  ) {
    this.mappingForm = this.fb.group({
      id: [null],
      bitFeatureId: [null, Validators.required],
      routerTemplateId: [null],
      targetStepId: [null],
      action: [FeatureRouterAction.AddStep, Validators.required],
      priority: [0],
      newStepSequence: [null],
      newStepWorkCenter: [''],
      newStepDescription: [''],
      newStepStandardTime: [null],
      newStepLongText: [''],
      modificationData: [''],
      condition: [''],
      isActive: [true]
    });
  }

  ngOnInit(): void {
    this.loadMappings();
    this.loadReferenceData();
  }

  // ============================================
  // Data Loading
  // ============================================

  loadMappings(): void {
    this.loading = true;
    this.routerAdminService.getMappings().subscribe({
      next: (mappings) => {
        this.mappings = mappings;
        this.applyFilters();
        this.loading = false;
      },
      error: (err) => {
        this.showNotification('Error loading mappings', 'error');
        this.loading = false;
      }
    });
  }

  loadReferenceData(): void {
    this.routerAdminService.getTemplates().subscribe({
      next: (templates) => this.templates = templates,
      error: () => this.templates = []
    });

    this.routerAdminService.getBitFeatures().subscribe({
      next: (features) => this.bitFeatures = features,
      error: () => this.bitFeatures = []
    });

    this.routerAdminService.getWorkCenters().subscribe({
      next: (wcs) => this.workCenters = wcs,
      error: () => {
        this.workCenters = [
          { code: 'MACH01', name: 'Machining Center 1' },
          { code: 'MACH02', name: 'Machining Center 2' },
          { code: 'ASSY01', name: 'Assembly Station 1' },
          { code: 'QC01', name: 'Quality Control' }
        ];
      }
    });
  }

  loadStepsForTemplate(templateId: number): void {
    this.routerAdminService.getSteps(templateId).subscribe({
      next: (steps) => this.steps = steps,
      error: () => this.steps = []
    });
  }

  // ============================================
  // Filtering
  // ============================================

  applyFilters(): void {
    let result = [...this.mappings];

    if (this.filterTemplateId) {
      result = result.filter(m => m.routerTemplateId === this.filterTemplateId);
    }

    if (this.filterFeatureId) {
      result = result.filter(m => m.bitFeatureId === this.filterFeatureId);
    }

    if (this.filterAction) {
      result = result.filter(m => m.action === this.filterAction);
    }

    if (this.searchText.trim()) {
      const search = this.searchText.toLowerCase();
      result = result.filter(m =>
        m.bitFeatureName?.toLowerCase().includes(search) ||
        m.newStepDescription?.toLowerCase().includes(search) ||
        m.targetStepDescription?.toLowerCase().includes(search)
      );
    }

    this.filteredMappings = result;
  }

  clearFilters(): void {
    this.filterTemplateId = null;
    this.filterFeatureId = null;
    this.filterAction = null;
    this.searchText = '';
    this.applyFilters();
  }

  // ============================================
  // CRUD Operations
  // ============================================

  createNewMapping(): void {
    this.selectedMapping = null;
    this.isNew = true;
    this.isEditing = true;
    this.steps = [];
    this.mappingForm.reset({
      action: FeatureRouterAction.AddStep,
      priority: 0,
      isActive: true
    });
  }

  editMapping(mapping: FeatureRouterMapping): void {
    this.selectedMapping = mapping;
    this.isNew = false;
    this.isEditing = true;
    this.mappingForm.patchValue(mapping);

    if (mapping.routerTemplateId) {
      this.loadStepsForTemplate(mapping.routerTemplateId);
    }
  }

  cancelEdit(): void {
    this.selectedMapping = null;
    this.isNew = false;
    this.isEditing = false;
    this.mappingForm.reset();
    this.steps = [];
  }

  saveMapping(): void {
    if (this.mappingForm.invalid) return;

    const mapping: FeatureRouterMapping = this.mappingForm.value;
    this.loading = true;

    if (this.isNew) {
      this.routerAdminService.createMapping(mapping).subscribe({
        next: (created) => {
          // Enrich with display names
          created.bitFeatureName = this.getBitFeatureName(created.bitFeatureId);
          created.routerTemplateName = this.getTemplateName(created.routerTemplateId);
          created.targetStepDescription = this.getStepDescription(created.targetStepId);

          this.mappings.push(created);
          this.applyFilters();
          this.cancelEdit();
          this.loading = false;
          this.showNotification('Mapping created successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error creating mapping', 'error');
          this.loading = false;
        }
      });
    } else {
      this.routerAdminService.updateMapping(mapping.id!, mapping).subscribe({
        next: (updated) => {
          // Enrich with display names
          updated.bitFeatureName = this.getBitFeatureName(updated.bitFeatureId);
          updated.routerTemplateName = this.getTemplateName(updated.routerTemplateId);
          updated.targetStepDescription = this.getStepDescription(updated.targetStepId);

          const index = this.mappings.findIndex(m => m.id === updated.id);
          if (index >= 0) this.mappings[index] = updated;
          this.applyFilters();
          this.cancelEdit();
          this.loading = false;
          this.showNotification('Mapping updated successfully', 'success');
        },
        error: (err) => {
          this.showNotification('Error updating mapping', 'error');
          this.loading = false;
        }
      });
    }
  }

  deleteMapping(mapping: FeatureRouterMapping): void {
    const dialogRef = this.dialogService.open({
      title: 'Confirm Delete',
      content: `Are you sure you want to delete this feature mapping?`,
      actions: [
        { text: 'Cancel' },
        { text: 'Delete', themeColor: 'error' }
      ],
      width: 400
    });

    dialogRef.result.subscribe((result: any) => {
      if (result.text === 'Delete') {
        this.routerAdminService.deleteMapping(mapping.id!).subscribe({
          next: () => {
            this.mappings = this.mappings.filter(m => m.id !== mapping.id);
            this.applyFilters();
            this.showNotification('Mapping deleted successfully', 'success');
          },
          error: (err) => {
            this.showNotification('Error deleting mapping', 'error');
          }
        });
      }
    });
  }

  duplicateMapping(mapping: FeatureRouterMapping): void {
    const newMapping = { ...mapping };
    delete newMapping.id;
    newMapping.isActive = true;

    this.routerAdminService.createMapping(newMapping).subscribe({
      next: (created) => {
        created.bitFeatureName = this.getBitFeatureName(created.bitFeatureId);
        created.routerTemplateName = this.getTemplateName(created.routerTemplateId);
        this.mappings.push(created);
        this.applyFilters();
        this.showNotification('Mapping duplicated successfully', 'success');
      },
      error: (err) => {
        this.showNotification('Error duplicating mapping', 'error');
      }
    });
  }

  // ============================================
  // Form Helpers
  // ============================================

  onTemplateChange(): void {
    const templateId = this.mappingForm.get('routerTemplateId')?.value;
    if (templateId) {
      this.loadStepsForTemplate(templateId);
    } else {
      this.steps = [];
    }
    this.mappingForm.patchValue({ targetStepId: null });
  }

  onActionChange(): void {
    const action = this.mappingForm.get('action')?.value;

    // Reset conditional fields based on action
    if (action === FeatureRouterAction.RemoveStep || action === FeatureRouterAction.SkipStep) {
      this.mappingForm.patchValue({
        newStepSequence: null,
        newStepWorkCenter: '',
        newStepDescription: '',
        newStepStandardTime: null,
        newStepLongText: ''
      });
    }

    if (action === FeatureRouterAction.AddStep) {
      this.mappingForm.patchValue({ targetStepId: null });
    }
  }

  requiresTargetStep(action: FeatureRouterAction): boolean {
    return [
      FeatureRouterAction.RemoveStep,
      FeatureRouterAction.ModifyStep,
      FeatureRouterAction.InsertBefore,
      FeatureRouterAction.InsertAfter,
      FeatureRouterAction.ReplaceStep,
      FeatureRouterAction.SkipStep,
      FeatureRouterAction.AppendInstructions
    ].includes(action);
  }

  requiresNewStepData(action: FeatureRouterAction): boolean {
    return [
      FeatureRouterAction.AddStep,
      FeatureRouterAction.InsertBefore,
      FeatureRouterAction.InsertAfter,
      FeatureRouterAction.ReplaceStep
    ].includes(action);
  }

  requiresModificationData(action: FeatureRouterAction): boolean {
    return action === FeatureRouterAction.ModifyStep;
  }

  requiresInstructionText(action: FeatureRouterAction): boolean {
    return action === FeatureRouterAction.AppendInstructions;
  }

  // ============================================
  // Display Helpers
  // ============================================

  getActionLabel(action: FeatureRouterAction): string {
    return this.actionLabels[action] || 'Unknown';
  }

  getActionIcon(action: FeatureRouterAction): string {
    const option = this.actionOptions.find(o => o.value === action);
    return option?.icon || 'k-i-gear';
  }

  getActionColor(action: FeatureRouterAction): string {
    switch (action) {
      case FeatureRouterAction.AddStep:
      case FeatureRouterAction.InsertBefore:
      case FeatureRouterAction.InsertAfter:
        return 'var(--kendo-color-success, #22c55e)';
      case FeatureRouterAction.RemoveStep:
        return 'var(--kendo-color-error, #ef4444)';
      case FeatureRouterAction.ModifyStep:
      case FeatureRouterAction.ReplaceStep:
        return 'var(--kendo-color-warning, #f59e0b)';
      case FeatureRouterAction.SkipStep:
        return 'var(--kendo-color-subtle, #64748b)';
      case FeatureRouterAction.AppendInstructions:
        return 'var(--kendo-color-info, #0284c7)';
      default:
        return 'var(--kendo-color-subtle, #64748b)';
    }
  }

  getBitFeatureName(id: number): string {
    const feature = this.bitFeatures.find(f => f.id === id);
    return feature ? `${feature.code} - ${feature.name}` : `Feature #${id}`;
  }

  getTemplateName(id: number | undefined): string {
    if (!id) return 'All Templates';
    const template = this.templates.find(t => t.id === id);
    return template?.name || `Template #${id}`;
  }

  getStepDescription(id: number | undefined): string {
    if (!id) return '';
    const step = this.steps.find(s => s.id === id);
    return step?.description || `Step #${id}`;
  }

  private showNotification(message: string, type: 'success' | 'error' | 'warning' | 'info'): void {
    this.notificationService.show({
      content: message,
      type: { style: type, icon: true },
      position: { horizontal: 'right', vertical: 'top' },
      animation: { type: 'fade', duration: 400 },
      hideAfter: 3000
    });
  }
}
